import { entities, reports } from '@/modules/catalog'
import { canShowReportItem } from '@/shared/components/sidebarNav'

export const customerMoneyPermissions = ['finance.view_receipt', 'finance.view_customerreceiptrecord'] as const
export const customerRefundPermissions = ['finance.view_customerrefund', 'finance.view_receipt'] as const
export const customerStatementPermissions = ['finance.view_customerstatement', 'finance.view_receivable',
  'finance.view_receipt', 'finance.view_customercreditnote', 'finance.view_customerrefund',
  'finance.view_customerreceiptcorrection', 'finance.view_customerrefundreversal'] as const
import { invoiceReadPermissions } from '@/modules/finance/invoiceForm'
import { paymentReadPermissions } from '@/modules/finance/paymentForm'
import { supplierActivityPermissions } from '@/modules/finance/supplierActivity'
import { creditReadPermissions } from '@/modules/finance/supplierCredit'
import { refundReadPermissions } from '@/modules/finance/supplierRefund'
import { feeReadPermissions } from '@/modules/procurement/landedCostForm'
import { returnReadPermissions } from '@/modules/procurement/supplierReturnForm'
import { freightReadPermissions } from '@/modules/sales/freightForm'
import { aftersaleReadPermissions } from '@/modules/sales/aftersalesForm'

export type PermissionCheck = (permission: string) => boolean
export type RouteAccessDecision = 'allow' | 'forbidden' | 'not-found'

interface RouteTarget {
  name?: string | symbol | null
  entityKey?: string
  reportKey?: string
  isStaff?: boolean
}

export function canAccessReport(reportKey: string, can: PermissionCheck): boolean {
  const report = reports.find((item) => item.key === reportKey)
  return Boolean(report && canShowReportItem(report, can, { forNav: false }))
}

export function evaluateRouteAccess(target: RouteTarget, can: PermissionCheck): RouteAccessDecision {
  if (target.name === 'reporting-rules') return can('reporting.view_reportingrule') ? 'allow' : 'forbidden'
  if (target.name === 'reporting-alerts') return can('reporting.view_reportingalert') ? 'allow' : 'forbidden'
  if (target.name === 'reports-location-map') return canAccessReport('location-map', can) ? 'allow' : 'forbidden'
  for (const key of ['period-inventory', 'profitability', 'stock-analysis', 'scrap-loss']) {
    if (target.name === `reports-${key}`) return canAccessReport(key, can) ? 'allow' : 'forbidden'
  }
  if (target.name === 'customer-money') return customerMoneyPermissions.every(can) ? 'allow' : 'forbidden'
  if (target.name === 'customer-refunds') return customerRefundPermissions.every(can) ? 'allow' : 'forbidden'
  if (target.name === 'customer-statement') return customerStatementPermissions.every(can) ? 'allow' : 'forbidden'
  if (target.name === 'sales-after-sale-returns') return ['sales.view_salesaftersale', 'sales.view_salesoutbound',
    'sales.view_salesaftersalereturn', 'inventory.view_inventorybatch'].every(can) ? 'allow' : 'forbidden'
  if (target.name === 'sales-after-sale-replacements') return ['sales.view_salesaftersale',
    'sales.view_salesoutbound'].every(can) ? 'allow' : 'forbidden'
  if (target.name === 'sales-freight-corrections') return can('sales.view_salesfreightadjustment')
    && freightReadPermissions.every(can) ? 'allow' : 'forbidden'
  if (target.name === 'native-stocktakes' || target.name === 'physical-stocktakes')
    return can('warehouse_ops.view_stocktake') ? 'allow' : 'forbidden'
  if (target.name === 'processing-orders') return can('processing.view_processingorder') ? 'allow' : 'forbidden'
  if (target.name === 'sales-after-sales') return aftersaleReadPermissions.every(can) ? 'allow' : 'forbidden'
  if (target.name === 'native-transfers' || target.name === 'physical-transfers')
    return can('warehouse_ops.view_stocktransfer') ? 'allow' : 'forbidden'
  if (target.name === 'native-scraps' || target.name === 'physical-scraps')
    return can('warehouse_ops.view_scraporder') ? 'allow' : 'forbidden'
  if (target.name === 'sales-freights') return freightReadPermissions.every(can) ? 'allow' : 'forbidden'
  if (target.name === 'supplier-refunds') return refundReadPermissions.every(can) ? 'allow' : 'forbidden'
  if (target.name === 'supplier-credits') return creditReadPermissions.every(can) ? 'allow' : 'forbidden'
  if (target.name === 'supplier-returns') return returnReadPermissions.every(can) ? 'allow' : 'forbidden'
  if (target.name === 'landed-costs') return feeReadPermissions.every(can) ? 'allow' : 'forbidden'
  if (target.name === 'supplier-activity') return supplierActivityPermissions.every(can) ? 'allow' : 'forbidden'
  if (target.name === 'payment-instructions') return paymentReadPermissions.every(can) ? 'allow' : 'forbidden'
  if (target.name === 'supplier-invoices') return invoiceReadPermissions.every(can) ? 'allow' : 'forbidden'
  if (target.name === 'records') {
    const entity = target.entityKey ? entities[target.entityKey] : undefined
    if (!entity) return 'not-found'
    return can(entity.permission) ? 'allow' : 'forbidden'
  }
  if (target.name === 'reports') {
    const report = target.reportKey ? reports.find((item) => item.key === target.reportKey) : undefined
    if (!report) return 'not-found'
    return canAccessReport(report.key, can) ? 'allow' : 'forbidden'
  }
  if (target.name === 'approval-flows') {
    return can('workflow.view_approvalflow') && can('workflow.change_approvalflow') ? 'allow' : 'forbidden'
  }
  if (target.name === 'role-permissions') {
    return can('auth.view_group') || can('auth.change_group') ? 'allow' : 'forbidden'
  }
  if (target.name === 'user-admin') {
    return can('accounts.view_user') || can('accounts.change_user') || can('accounts.add_user') ? 'allow' : 'forbidden'
  }
  if (target.name === 'document-numbering') {
    return target.isStaff ? 'allow' : 'forbidden'
  }
  return 'allow'
}

export function firstAccessiblePath(can: PermissionCheck): string {
  const entity = Object.values(entities).find((item) => item.navigation !== false && can(item.permission))
  if (entity) return `/records/${entity.key}`
  const report = reports.find((item) => item.navigation !== false && canAccessReport(item.key, can))
  if (report) return `/reports/${report.key}`
  if (can('reporting.view_reportingalert')) return '/notifications'
  if (can('reporting.view_reportingrule')) return '/settings/inventory-analysis'
  return '/approvals'
}

export function safeNextPath(value: unknown, fallback: string): string {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//')) return fallback
  return value
}
